import React,{ useEffect, useRef, useState } from "react";
import styles from "../styles/VideoPlayer.module.css";

const VideoPlayer = ({ videoUrl, thumbnail }) => {
  const [playing, setPlaying] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // Extract YouTube video ID from url
  const getYoutubeId = (url) => {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  const videoId = getYoutubeId(videoUrl);

  // Load YouTube IFrame API
  useEffect(() => {
    if (!playing || !videoId) return;
    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = createPlayer;
    }
    function createPlayer() {
      if (playerRef.current || !iframeRef.current) return;
      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onReady: () => setPlayerReady(true),
        },
      });
    }
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [playing, videoId]);

  // Intersection Observer for pause only (no auto-play)
  useEffect(() => {
    if (!playing || !playerReady) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (
            playerRef.current &&
            typeof playerRef.current.pauseVideo === "function"
          ) {
            playerRef.current.pauseVideo();
          }
        }
      },
      { threshold: 0.5 }
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [playing, playerReady]);

  return (
    <div className={styles.videoContainer} ref={containerRef}>
      {!playing ? (
        <div
          className={styles.thumbnailWrapper}
          onClick={() => setPlaying(true)}
        >
          {thumbnail ? (
            <img
              src={thumbnail}
              alt="Video thumbnail"
              className={styles.thumbnail}
            />
          ) : (
            <div className={styles.defaultThumbnail} />
          )}
          <button className={styles.playButton} aria-label="Play video">
            ▶
          </button>
        </div>
      ) : (
        videoId && (
          <iframe
            ref={iframeRef}
            id={`ytplayer-${videoId}`}
            width="100%"
            height="325"
            src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        )
      )}
    </div>
  );
};

export default VideoPlayer;
